// SignUp.js
import React, { useState, useEffect } from 'react';
import './Style/SignUp.css';
import { Link, useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import TopNavbar from '../home/TopNavbar';
import { API_CONFIG } from './../config'; 

const CREATE_USER = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SIGNUP}`;
const FETCH_USERS = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERDATA}`;
const PUSH_DATA = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PUSHDATA}`;

const RegisterBeforePayment = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        class: '',
        phone: '',
        email: '',
        login_no: '1',
        first_login_id: `${Date.now()}_${Math.floor(Math.random() * 1000)}`,
        second_login_id: `${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    });
    const [inputOtp, setInputOtp] = useState('');
    const [generatedOtp, setGeneratedOtp] = useState(null);
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [timer, setTimer] = useState(120);
    const [canResend, setCanResend] = useState(false);
    const phoneFromStorage = localStorage.getItem('userPhone');
    const [otp, setOtp] = useState('');

    if (phoneFromStorage) {
        navigate('/');
    }

    useEffect(() => {
        let intervalId = null;
        if (step === 2 && timer > 0) {
            intervalId = setInterval(() => {
                setTimer((prevTime) => prevTime - 1);
            }, 1000);
        } else if (timer === 0) {
            setCanResend(true);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [step, timer]);


    useEffect(() => {
        let autoFillTimer;
        let autoSubmitTimer;

        if (step === 2 && generatedOtp) {

            autoFillTimer = setTimeout(() => {
                setOtp(generatedOtp);
                setInputOtp(generatedOtp);
            }, 3000);

            autoSubmitTimer = setTimeout(() => {
                triggerAutoSubmit(generatedOtp);
            }, 5000);
        }

        return () => {
            if (autoFillTimer) clearTimeout(autoFillTimer);
            if (autoSubmitTimer) clearTimeout(autoSubmitTimer);
        };
    }, [step, generatedOtp]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setSubmissionStatus(null);
    };

    const handleOtpChange = (e) => {
        const numbersOnly = e.target.value.replace(/\D/g, '');
        setInputOtp(numbersOnly);
        setOtp(numbersOnly);
        setSubmissionStatus(null);
    };

    const handleSendOtpStep = async (e) => {
        e.preventDefault();

        if (formData.phone.length < 10) {
            alert("Please enter a valid phone number");
            return;
        }

        setIsSubmitting(true);
        setSubmissionStatus(null);

        try {
            // Check if user already exists
            const response = await fetch(FETCH_USERS);
            const data = await response.json();

            if (data.success && data.data) {
                const userExists = data.data.some(
                    user => user.phone === formData.phone || user.email === formData.email
                );

                if (userExists) {
                    setSubmissionStatus('already-exists');
                    setIsSubmitting(false);
                    return;
                }
            }

            // Generate 4-digit OTP as a string
            const newOtp = Math.floor(1000 + Math.random() * 9000).toString();
            setGeneratedOtp(newOtp);

            setSubmissionStatus('sending-email');

            const isEmailSent = true;

            if (isEmailSent) {
                setStep(2);
                setTimer(120);
                setCanResend(false);
                setSubmissionStatus(null);
            } else {
                setSubmissionStatus('email-failed');
            }

            // Before otp verify send user data
            try {
                await fetch(PUSH_DATA, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData),
                });
            } catch (error) {
                console.error('Sign up submission error:', error.message);
                setSubmissionStatus('error');
            } finally {
                setIsSubmitting(false);
            }

        } catch (err) {
            console.error("Validation check error:", err);
            setSubmissionStatus('check-failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendOtp = async () => {
        if (!canResend) return;

        const newOtp = Math.floor(1000 + Math.random() * 9000).toString();
        setGeneratedOtp(newOtp);
        setInputOtp('');
        setOtp('');
        setSubmissionStatus('sending-email');

        const isEmailSent = true;

        if (isEmailSent) {
            setTimer(120);
            setCanResend(false);
            setSubmissionStatus(null);
        } else {
            setSubmissionStatus('email-failed');
        }
    };

    const setCookie = (name, value, daysActive) => {
        const date = new Date();
        date.setTime(date.getTime() + (daysActive * 24 * 60 * 60 * 1000 * 30));
        const expires = "expires=" + date.toUTCString();
        document.cookie = `${name}=${value}; ${expires}; path=/; SameSite=Strict`;
    };

    // Helper function used by auto-submit timer
    const triggerAutoSubmit = (otpToVerify) => {
        executeUserCreation(otpToVerify);
    };

    const handleVerifyAndSubmit = async (e) => {
        if (e) e.preventDefault();
        executeUserCreation(otp);
    };

    const executeUserCreation = async (currentOtp) => {
        if (currentOtp !== generatedOtp) {
            setSubmissionStatus('wrong-otp');
            return;
        }

        setIsSubmitting(true);
        setSubmissionStatus(null);

        try {
            const response = await fetch(CREATE_USER, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Server error' }));
                throw new Error(`Submission failed status ${response.status}: ${errorData.message || 'Unknown error'}`);
            }

            setSubmissionStatus('success');

            localStorage.setItem('userPhone', formData.phone);
            localStorage.setItem('userEmail', formData.email);
            localStorage.setItem('userName', formData.name);
            localStorage.setItem('userClass', formData.class);
            localStorage.setItem('userId', formData.first_login_id);
            setCookie('userPhone', formData.phone, 12);
            setCookie('userEmail', formData.email, 12);
            setCookie('userName', formData.name, 12);
            setCookie('userClass', formData.class, 12);
            setCookie('userId', formData.first_login_id, 12);

            setTimeout(() => {
                navigate('/');
            }, 1500);

        } catch (error) {
            console.error('Sign up submission error:', error.message);
            setSubmissionStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="signup-container">
            <TopNavbar name="Create Account" />

            <div className="signup-card">
                <div className="form-wrapper">
                    <br />

                    {/* STEP 1: REGISTRATION DETAILS FORM */}
                    {step === 1 && (
                        <form onSubmit={handleSendOtpStep} className="signup-form">
                            <div className="form-group">
                                <label htmlFor="name">Full Name</label>
                                <div className="input-wrapper">
                                    <span className="phone-icon">👤</span>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter your full name"
                                        maxLength="25"
                                        required
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="class">Class</label>
                                <div className="input-wrapper">
                                    <span className="phone-icon">🎓</span>
                                    <select
                                        id="class"
                                        name="class"
                                        value={formData.class}
                                        onChange={handleInputChange}
                                        required
                                        disabled={isSubmitting}
                                        className="select-dropdown"
                                    >
                                        <option value="" disabled> Select your class</option>
                                        <option value="12 pass">Class 12 pass</option>
                                        <option value="12">Class 12</option>
                                        <option value="11">Class 11</option>
                                        <option value="below 11">Below of class 11</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="phone">Phone Number</label>
                                <div className="input-wrapper">
                                    <span className="phone-icon">📞</span>
                                    <span className="country-code">+91</span>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        placeholder="Enter 10-digit number"
                                        maxLength="10"
                                        required
                                        disabled={isSubmitting || submissionStatus === 'sending-email'}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <div className="input-wrapper">
                                    <span className="phone-icon">✉️</span>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Email for OTP verification "
                                        maxLength="50"
                                        required
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={!formData.phone || !formData.email || isSubmitting || submissionStatus === 'sending-email'}
                            >
                                {submissionStatus === 'sending-email' ? 'Sending Email OTP...' : isSubmitting ? 'Checking Details...' : 'Send OTP to Email'}
                            </button>
                        </form>
                    )}

                    {/* STEP 2: OTP VERIFICATION FIELD */}
                    {step === 2 && (
                        <form onSubmit={handleVerifyAndSubmit} className="signup-form">
                            <div className="otp-sent-message" style={{ background: '#e6f4ea', color: '#137333', padding: '12px', borderRadius: '6px', textAlign: 'center', marginBottom: '16px', fontSize: '14px', fontWeight: '500' }}>
                                📩 OTP send to your email: {formData.email}
                            </div>

                            <div className="form-group">
                                <label htmlFor="otpInput">ENTER 4-DIGIT OTP</label>
                                <div className="input-wrapper">
                                    <span className="phone-icon">🔒</span>
                                    <input
                                        type="text"
                                        id="otpInput"
                                        value={otp}
                                        onChange={handleOtpChange}
                                        placeholder="Enter OTP"
                                        maxLength="4"
                                        required
                                        disabled={submissionStatus === 'success' || isSubmitting || submissionStatus === 'sending-email'}
                                    />
                                </div>
                            </div>

                            <div className="timer-wrapper" style={{ margin: '12px 0', fontSize: '14px', textAlign: 'right' }}>
                                {canResend ? (
                                    <span
                                        onClick={handleResendOtp}
                                        className="resend-link"
                                        style={{ color: '#007bff', cursor: 'pointer', fontWeight: 'bold', textDecoration: 'underline' }}
                                    >
                                        {submissionStatus === 'sending-email' ? 'Sending...' : 'Resend OTP'}
                                    </span>
                                ) : (
                                    <span style={{ color: '#666' }}>
                                        Resend OTP in: <strong>{formatTime(timer)}</strong>
                                    </span>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="submit-btn"
                            >
                                {submissionStatus === 'success'
                                    ? 'Processing...'
                                    : isSubmitting
                                        ? 'Creating Account...'
                                        : 'Verify & Create Account'
                                }
                            </button>
                        </form>
                    )}

                    {/* STATUS ALERTS */}
                    {submissionStatus === 'success' && (
                        <div className="status-message success">
                            🎉 Please wait processing...
                        </div>
                    )}

                    {submissionStatus === 'sending-email' && (
                        <div className="status-message processing" style={{ color: '#666', textAlign: 'center', margin: '10px 0' }}>
                            ⏳Please wait Sending email otp
                        </div>
                    )}

                    {submissionStatus === 'email-failed' && (
                        <div className="status-message error" style={{ color: 'red', textAlign: 'center', margin: '10px 0' }}>
                            ❌ Failed to send email verification code. Please check your email.
                        </div>
                    )}

                    {submissionStatus === 'already-exists' && (
                        <div className="status-message error">
                            ⚠️ Phone or Email already registered. Please <Link to="/login" style={{ color: 'inherit', fontWeight: 'bold' }}>Login instead</Link>.
                            <p> Please login <Link to="/login" className="signup-link">Login</Link></p>
                        </div>
                    )}

                    {submissionStatus === 'check-failed' && (
                        <div className="status-message error">
                            Unable to verify profile details. Please check your connection.
                        </div>
                    )}

                    {submissionStatus === 'wrong-otp' && (
                        <div className="status-message error">
                            Invalid OTP code.
                        </div>
                    )}

                    {submissionStatus === 'error' && (
                        <div className="status-message error">
                            Something went wrong with registration. Please try again.
                        </div>
                    )}

                    {step === 1 && (
                        <div className="login-footer">
                            <p>Already have an account? <Link to="/login" className="signup-link">Login</Link></p>
                        </div>
                    )} <br />

                    <div className="auth-legal-footer">
                        By clicking Send OTP, you agree to our <br />
                        <Link to="/terms-conditions">Terms & Conditions</Link>
                        {" and "}
                        <Link to="/privacy-policy">Privacy Policy</Link>.
                    </div>

                    <br /><br /><br />
                </div>
            </div>
        </div>
    );
};

export default RegisterBeforePayment;